// The signed-in chassis, mounted ONCE, and the one place session boundaries are
// enforced (PRD 18 US-151 / US-152).
//
// WHY THIS EXISTS. Every shell-bearing route used to render its own
// `<AppShell>`. React therefore tore one shell down and mounted the next on
// every navigation — measured, by watching a marker attribute on
// `.maisight-shell-sidebar` disappear across a round trip — so sidebar state,
// scroll position and any shell-level transition reset on each click. That
// defeats US-112's "shell not remounted across a route change" and makes the
// browser-back beat (walkthrough beat 7) unobservable.
//
// It also collected the access check, which lived on `/dashboard/$orgSlug`
// alone: every other shell-bearing URL rendered its panels for a signed-out
// caller and let the fetches 401 one by one. One gate, seventeen screens.
//
// PATHLESS, ON PURPOSE. The route carries an `id` and no `path`, so it
// contributes no URL segment and every child keeps the exact `fullPath` it had
// before — `frontend/tests/routes.test.ts` asserts the registered set in both
// directions and must not need loosening for a layout change.
//
// The six S1 auth frames and `/no-access` are NOT children of this route. They
// are pre-login (or, for `/no-access`, scoped to an org the URL does not name),
// and adding chrome to them is wrong rather than an omission.

import {
  Navigate,
  Outlet,
  createRoute,
  useMatches,
  useParams,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AppShell } from "../shell/AppShell";
import { orgNav } from "../shell/hrefs";
import { signOut, useOrgAccess } from "../screens/overview";
import { rememberNext } from "../screens/signin/nextUrl";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** Trailing breadcrumb segment shown in the topbar. */
    title?: string;
  }
}

export const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  component: ShellLayout,
});

function ShellLayout() {
  // `strict: false` because one layout serves routes with different param and
  // search shapes: the dashboard family names its org in the path, and
  // `/docs/$docId/review` carries it as `?org=` because a document id yields no
  // org (see `screens/content/GlossaryReviewScreen.tsx`).
  const params = useParams({ strict: false }) as { orgSlug?: string };
  const search = useSearch({ strict: false }) as { org?: string };
  const matches = useMatches();

  const orgSlug = params.orgSlug ?? search.org ?? null;
  const title = matches
    .map((match) => match.staticData?.title)
    .filter((value): value is string => Boolean(value))
    .pop();

  // No org means no sidebar to point anywhere honest. That is the pre-PRD-18
  // rendering of `/docs/:id/review`, and the safest fallback for the flow that
  // must not break.
  if (!orgSlug) return <Outlet />;

  return <ShellGate orgSlug={orgSlug} title={title} />;
}

function ShellGate({ orgSlug, title }: { orgSlug: string; title?: string }) {
  const access = useOrgAccess(orgSlug);
  const href = useRouterState({
    select: (state) => state.location.href,
  });

  // No credential at all is a sign-in problem, not an access problem — frame 10
  // is for a signed-IN user who reached an org that is not theirs. The same
  // branch covers a session that expired mid-navigation: `GET /auth/me` starts
  // answering 401 and the next route change lands on `/signin` rather than on a
  // half-rendered screen or a spinner that never resolves.
  if (access === "signed-out") {
    rememberNext(href);
    return <Navigate to="/signin" replace />;
  }
  if (access === "denied") return <Navigate to="/no-access" replace />;

  // `checking` still renders the body: each screen already shows its own
  // loading state from its own queries, and swapping in a shell-level skeleton
  // would remount the body on every arrival.
  return (
    <AppShell nav={orgNav(orgSlug)} title={title} onSignOut={signOut}>
      <Outlet />
    </AppShell>
  );
}
